import { makeAutoObservable, action } from "mobx";
import { DateTime } from "luxon";
import _ from "lodash";

import { getMonitorEvents } from "shared/../serviceCalls";
import { formatDate } from "shared/dateTimeUtils";
import config from "shared/../config";

export const severities = ["high", "medium", "low"];

export default class EventsStore {
  _query = undefined;
  severity = null;
  loadingEvents = false;
  eventsObj = null;
  selectedEvent;
  _liveMode = false;
  _scrollTop;
  pollingEvents;
  historyComplete = false;
  selectedSeverities = new Set();
  showFilters = false;
  pageFilled = false;
  expandedSegments = {
    "Last Hour": false,
    "1–6 Hours Ago": false,
    "More than 6 Hours Ago": false,
  };
  constructor({ monitorStore, appStore, asmpid, isPage }) {
    Object.assign(this, {
      monitorStore,
      appStore,
      asmpid: asmpid || monitorStore?.asmpid,
      isPage,
    });
    makeAutoObservable(this);
  }

  get minDateTime() {
    const hours =
      this.appStore?.prefs?.userPreferences?.server?.pm?.dataRangeInHours ||
      24 * 30;
    return DateTime.utc().minus({ hours });
  }

  get query() {
    return this._query;
  }
  set query(value) {
    this._query = value;
  }

  get filterCount() {
    return !!this.query + !!this.selectedSeverities.size;
  }

  submit(viewModel) {
    const { selectedSeverities, query, time } = viewModel;
    Object.assign(this, { selectedSeverities, query, time });
    const eventTime = time?.toString().match(/invalid/i) ? undefined : time;
    if (eventTime) this.liveMode = false;
    return this.fetchPmEvents({ reset: true, eventTime });
  }

  buildQuery({ query, eventTime }) {
    const { asmpid } = this;
    const messagefilter = this.query?.trim() || undefined;
    const levelfilter = this.selectedSeverities.size
      ? [...this.selectedSeverities]
      : undefined;
    const apiQueries = [];
    const _query = {
      count: -config.pm.events.count,
      messagefilter,
      levelfilter,
      asmpid,
      discretes: !!this.isPage,
      ...query,
    };
    apiQueries.push(_query);
    if (eventTime) {
      try {
        const fromtime = DateTime.fromISO(eventTime);
        if (!fromtime?.isValid) throw "invalid timstamp:" + eventTime;
        _query.fromtime = fromtime;
        this.liveMode = false;
        apiQueries.push({
          ..._query,
          count: config.pm.events.count,
          fromtime: _query.fromtime?.plus({ seconds: 1 }),
        });
      } catch (e) {
        console.error("failed to parse timestamp", e);
      }
    }
    return apiQueries;
  }

  async fetchPmEvents({ polling, reset, query, eventTime } = {}) {
    if (reset) {
      this.eventsObj = undefined;
      this.historyComplete = false;
    }
    if (polling) {
      this.pollingEvents = polling;
    } else {
      this.loadingEvents = true;
    }

    const apiQueries = this.buildQuery({ query, eventTime });
    const hasMultiday = await this.appStore.hasMultiday;
    let ressponses;
    try {
      ressponses = await Promise.all(
        apiQueries.map((_query) => getMonitorEvents(_query)),
      );
      ressponses.forEach((res, i) => {
        this.loadEvents({ res, query: apiQueries[i], hasMultiday });
      });
    } catch (e) {
      console.error(e);
    } finally {
      this.loadingEvents = false;
      this.pollingEvents = false;
    }
    return ressponses;
  }
  loadEvents = action(function loadEvents({ res, query, hasMultiday }) {
    if (this.isDead) return;
    // transform
    let eventData = res?.eventData.map((pmEvent) => {
      const dateTime = DateTime.fromISO(pmEvent.eventTime).toUTC();
      let discreteData;
      if (pmEvent.discreteData?.length && res.discreteGroups) {
        discreteData = pmEvent.discreteData?.map((datum) => ({
          ...datum,
          group: res.discreteGroups.find((group) =>
            group.keys.includes(datum.key),
          ),
        }));
      }
      return {
        ...pmEvent,
        dateTime,
        discreteData,
        day: formatDate(dateTime),
      };
    });

    const invalidateCache =
      this.eventsObj?.allEventsInfo?.oldestEventTimestamp &&
      !res.allEventsInfo?.oldestEventTimestamp;

    // merge events.
    if (hasMultiday && this.eventsObj?.eventData?.length && !invalidateCache) {
      if (query.fromtime && query.count < 0) {
        // historical
        eventData = [...this.eventsObj.eventData, ...eventData];
      } else if (query.fromtime && query.count > 0) {
        //live
        eventData = [...eventData, ...this.eventsObj.eventData];
      }
    }
    this.eventsObj = {
      ...res,
      eventData,
    };
    if (!this.historyComplete) {
      const loadingPrevious = query.count < 0;
      if (loadingPrevious || !query.fromtime) {
        this.historyComplete = !res?.eventData?.length;
      }
    }

    if (res?.allEventsInfo?.oldestEventTimestamp) {
      // Clean up history.
      const index = this.eventsObj.eventData.findIndex(
        (event) => event.eventTime < res.allEventsInfo?.oldestEventTimestamp,
      );
      if (index !== -1)
        this.eventsObj.eventData = this.eventsObj.eventData.slice(0, index);
    }
  });

  fetchHistory = _.throttle(({ later } = {}) => {
    if (!later && this.historyComplete) return;
    if (later && this.liveMode) return;
    let query;
    const { count } = config.pm.events;
    if (later) {
      query = {
        fromtime: this.eventsObj?.eventData?.[0]?.dateTime.plus({ seconds: 1 }),
        count,
      };
    } else {
      query = {
        fromtime: this.eventsObj?.eventData?.lastItem?.dateTime.minus({
          seconds: 1,
        }),
        count: -count,
      };
    }
    return this.fetchPmEvents({
      query,
      polling: later ? "later" : "history",
    }).then(([res]) => {
      if (later && !res?.eventData?.length) {
        this.liveMode = true;
      }
    });
  }, 1000);

  get events() {
    return this.eventsObj;
  }

  get liveMode() {
    return this._liveMode;
  }
  set liveMode(liveMode) {
    const wasLive = this._liveMode;
    if (liveMode) {
      this.selectedEvent = null;
      this.time = undefined;
    } else {
      clearTimeout(this.liveTimer);
    }
    this._liveMode = liveMode;
    if (liveMode && !wasLive) {
      this.eventsObj = undefined;
      this.historyComplete = false;
      this.pollLatest();
    }
  }

  get eventSegments() {
    if (this.isPage) return this.eventSegmentsPage;
    const events = this.eventsObj?.eventData;
    if (!events?.length) return [];
    let lastIndex = 0;
    const segments = [];
    const titles = ["Past 6 hours", "6–12 Hours Ago", "12–24 Hours Ago"];
    const now = this.monitorStore?.serverTime || DateTime.utc();
    [6, 12, 24].forEach((hours, i) => {
      if (lastIndex >= events.length - 1) return;
      const minTime = now.minus({ hours });
      let segmentLength = events
        .slice(lastIndex)
        .findIndex((event) => event.dateTime < minTime);
      if (segmentLength === -1) {
        segmentLength = events.length;
      }
      if (segmentLength > 0) {
        const nextIndex = lastIndex + segmentLength;
        segments.push({
          title: titles[i],
          events: events.slice(lastIndex, nextIndex),
        });
        lastIndex = nextIndex;
      }
    });
    const today = formatDate(now);
    let lastDate = today;
    const firstDateIndex = lastIndex;
    events.slice(firstDateIndex).forEach((event, i) => {
      if (event.day !== lastDate) {
        if (i > 0) {
          const nextIndex = firstDateIndex + i;
          segments.push({
            title: lastDate,
            events: events.slice(lastIndex, nextIndex),
          });
          lastIndex = nextIndex;
        }
        lastDate = event.day;
      }
    });
    const lastEvents = events.slice(lastIndex);
    if (lastEvents.length) {
      segments.push({
        title: lastDate,
        events: events.slice(lastIndex),
      });
    }
    return segments;
  }
  get eventSegmentsPage() {
    const events = this.events?.eventData;
    const anHourAgo = DateTime.local().minus({ hours: 1 });
    const sixHoursAgo = DateTime.local().minus({ hours: 6 });
    return [
      {
        title: "Last Hour",
        events: events.filter(
          (event) => DateTime.fromISO(event.eventTime) >= anHourAgo,
        ),
      },
      {
        title: "1–6 Hours Ago",
        events: events.filter(
          (event) =>
            DateTime.fromISO(event.eventTime) < anHourAgo &&
            DateTime.fromISO(event.eventTime) >= sixHoursAgo,
        ),
      },
      {
        title: "More than 6 Hours Ago",
        events: events.filter(
          (event) => DateTime.fromISO(event.eventTime) < sixHoursAgo,
        ),
      },
    ].map((segment) => ({
      ...segment,
      expanded: this.expandedSegments[segment.title],
    }));
  }

  openFilters = (e) => {
    e?.preventDefault();
    this.showFilters = true;
    // todo setup filters.
  };

  async pollLatest() {
    clearTimeout(this.liveTimer);
    if (!this.liveMode) return;
    this.liveTimer = setTimeout(() => {
      const fromtime = this.eventsObj?.eventData?.[0]?.dateTime
        .plus({ seconds: 1 })
        .toUTC();
      const query = fromtime?.isValid
        ? {
            fromtime: fromtime.toISO({ suppressMilliseconds: true }),
            count: 1000,
          }
        : {};
      this.fetchPmEvents({
        query,
        polling: "live",
      }).finally(() => this.pollLatest());
    }, config.pm.events.pollingFrequencySeconds * 1000);
  }

  destroy() {
    this.isDead = true;
    clearTimeout(this.liveTimer);
    this.pollLatest = () => {};
  }

  getClosest(date) {
    const dateMs = +new Date(date);
    const index = this.eventsObj.eventData.findIndex(
      (event) => event.dateTime <= dateMs,
    );
    if (index === -1) return this.eventsObj.eventData.lastItem;
    let selectedEvent = this.eventsObj.eventData[index];
    const prevEvent = this.eventsObj.eventData[index + 1];
    if (
      prevEvent &&
      dateMs - prevEvent.dateTime > selectedEvent.dateTime - dateMs
    ) {
      selectedEvent = prevEvent;
    }
    return selectedEvent;
  }
}
